// Main API service - automatically switches between static and FastAPI modes
// Mode controlled by REACT_APP_USE_STATIC_DATA environment variable

import { staticDataService } from './staticDataService';
import { fastApiService } from './fastApiService';

const useStaticData = process.env.REACT_APP_USE_STATIC_DATA === 'true';
const selectedService = useStaticData ? staticDataService : fastApiService;

const withFallback = async (primaryFn, fallbackFn, ...args) => {
  try {
    return await primaryFn(...args);
  } catch (error) {
    console.warn('Primary service failed, falling back to static data:', error);
    return await fallbackFn(...args);
  }
};

export const api = {
  getAll: (entity, params = {}) => {
    if (useStaticData) {
      return staticDataService.getAll(entity, params);
    }
    return withFallback(
      () => fastApiService.getAll(entity, params),
      () => staticDataService.getAll(entity, params)
    );
  },
  getById: (entity, id) => {
    if (useStaticData) {
      return staticDataService.getById(entity, id);
    }
    return withFallback(
      () => fastApiService.getById(entity, id),
      () => staticDataService.getById(entity, id)
    );
  },
  create: (entity, data) => {
    if (useStaticData) {
      return staticDataService.create(entity, data);
    }
    return withFallback(
      () => fastApiService.create(entity, data),
      () => staticDataService.create(entity, data)
    );
  },
  update: (entity, id, data) => {
    if (useStaticData) {
      return staticDataService.update(entity, id, data);
    }
    return withFallback(
      () => fastApiService.update(entity, id, data),
      () => staticDataService.update(entity, id, data)
    );
  },
  delete: (entity, id) => {
    if (useStaticData) {
      return staticDataService.delete(entity, id);
    }
    return withFallback(
      () => fastApiService.delete(entity, id),
      () => staticDataService.delete(entity, id)
    );
  },
  search: (entity, query, params = {}) => {
    if (useStaticData) {
      return staticDataService.search ? staticDataService.search(entity, query, params) : staticDataService.getAll(entity, params);
    }
    return withFallback(
      () => fastApiService.search(entity, query, params),
      () => staticDataService.getAll(entity, params)
    );
  }
};

// Entity-specific helper functions
export const getUsers = (params) => api.getAll('users', params);
export const getUserById = (id) => api.getById('users', id);
export const createUser = (data) => api.create('users', data);
export const updateUser = (id, data) => api.update('users', id, data);
export const deleteUser = (id) => api.delete('users', id);
export const searchUsers = (q, params) => api.search('users', q, params);

export const getRoles = (params) => api.getAll('roles', params);
export const getRoleById = (id) => api.getById('roles', id);
export const createRole = (data) => api.create('roles', data);
export const updateRole = (id, data) => api.update('roles', id, data);
export const deleteRole = (id) => api.delete('roles', id);
export const searchRoles = (q, params) => api.search('roles', q, params);

export const getUserRoles = (params) => api.getAll('user_roles', params);
export const createUserRole = (data) => api.create('user_roles', data);
export const deleteUserRole = (id) => api.delete('user_roles', id);

export const getOrganizations = (params) => api.getAll('organizations', params);
export const getOrganizationById = (id) => api.getById('organizations', id);
export const createOrganization = (data) => api.create('organizations', data);
export const updateOrganization = (id, data) => api.update('organizations', id, data);
export const deleteOrganization = (id) => api.delete('organizations', id);
export const searchOrganizations = (q, params) => api.search('organizations', q, params);

export const getPortfolios = (params) => api.getAll('portfolios', params);
export const getPortfolioById = (id) => api.getById('portfolios', id);
export const createPortfolio = (data) => api.create('portfolios', data);
export const updatePortfolio = (id, data) => api.update('portfolios', id, data);
export const deletePortfolio = (id) => api.delete('portfolios', id);
export const searchPortfolios = (q, params) => api.search('portfolios', q, params);

export const getProducts = (params) => api.getAll('products', params);
export const getProductById = (id) => api.getById('products', id);
export const createProduct = (data) => api.create('products', data);
export const updateProduct = (id, data) => api.update('products', id, data);
export const deleteProduct = (id) => api.delete('products', id);
export const searchProducts = (q, params) => api.search('products', q, params);

export const getInventoryLogs = (params) => api.getAll('inventory_logs', params);
export const createInventoryLog = (data) => api.create('inventory_logs', data);
export const updateInventoryLog = (id, data) => api.update('inventory_logs', id, data);
export const deleteInventoryLog = (id) => api.delete('inventory_logs', id);

export const getPosts = (params) => api.getAll('posts', params);
export const getPostById = (id) => api.getById('posts', id);
export const createPost = (data) => api.create('posts', data);
export const updatePost = (id, data) => api.update('posts', id, data);
export const deletePost = (id) => api.delete('posts', id);

export const getComments = (params) => api.getAll('comments', params);
export const createComment = (data) => api.create('comments', data);
export const deleteComment = (id) => api.delete('comments', id);

export const getNotifications = (params) => api.getAll('notifications', params);
export const getNotificationById = (id) => api.getById('notifications', id);
export const createNotification = (data) => api.create('notifications', data);
export const updateNotification = (id, data) => api.update('notifications', id, data);
export const deleteNotification = (id) => api.delete('notifications', id);

export const getEvents = (params) => api.getAll('events', params);
export const getEventById = (id) => api.getById('events', id);
export const createEvent = (data) => api.create('events', data);
export const deleteEvent = (id) => api.delete('events', id);

export const getFiles = (params) => api.getAll('files', params);
export const getFileById = (id) => api.getById('files', id);
export const createFile = (data) => api.create('files', data);
export const deleteFile = (id) => api.delete('files', id);

export const getCategories = (params) => api.getAll('categories', params);
export const getCategoryById = (id) => api.getById('categories', id);
export const createCategory = (data) => api.create('categories', data);
export const updateCategory = (id, data) => api.update('categories', id, data);
export const deleteCategory = (id) => api.delete('categories', id);

export const getReports = (params) => api.getAll('reports', params);
export const createReport = (data) => api.create('reports', data);
export const updateReport = (id, data) => api.update('reports', id, data);
export const deleteReport = (id) => api.delete('reports', id);

export const getIntegrations = (params) => api.getAll('integrations', params);
export const createIntegration = (data) => api.create('integrations', data);
export const updateIntegration = (id, data) => api.update('integrations', id, data);
export const deleteIntegration = (id) => api.delete('integrations', id);

// Authentication helpers with auto-fallback
export const login = (credentials) => {
  if (useStaticData) {
    console.log('Static mode: Simulated login');
    return Promise.resolve({ token: 'demo-token', user: { id: 1, name: 'Demo User' } });
  }
  return withFallback(
    () => fastApiService.login(credentials),
    () => Promise.resolve({ token: 'demo-token', user: { id: 1, name: 'Demo User' } })
  );
};

export const logout = () => {
  if (useStaticData) {
    console.log('Static mode: Simulated logout');
    return Promise.resolve({ success: true });
  }
  return withFallback(
    () => fastApiService.logout(),
    () => Promise.resolve({ success: true })
  );
};

export const apiConfig = {
  getCurrentConfig: () => {
    if (useStaticData) {
      return { mode: 'static', endpoint: 'public/data/*.json' };
      }
    return { mode: 'api', endpoint: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api' };
  },
  isStaticMode: () => useStaticData
};

export default api;
